class PdpRedesign {
  constructor(root) {
    this.root = root;
    this.galleryTrack = root.querySelector('[data-pdp-gallery-track]');
    this.thumbs = [...root.querySelectorAll('[data-pdp-gallery-thumb]')];
    this.desktopItems = [...root.querySelectorAll('[data-pdp-gallery-desktop-item]')];
    this.configureCards = [...root.querySelectorAll('[data-pdp-configure-card]')];
    this.videoModal = root.querySelector('[data-pdp-video-modal]');
    this.videoMedia = root.querySelector('[data-pdp-video-media]');
    this.videoProductCard = root.querySelector('[data-pdp-video-product-card]');
    this.compareModal = root.querySelector('[data-pdp-compare-modal]');
    this.productForm = root.querySelector('form[id^="product-form"]');
    this.variantInput = root.querySelector('[data-pdp-variant-input]');
    this.ctaMode = root.classList.contains('pdp-redesign--preorder') ? 'preorder' : 'buy';
    this.selectedAddons = [];
    this.videoTriggers = [...root.querySelectorAll('[data-pdp-video-trigger]')];
    this.variants = this.parseJson('[data-pdp-variants-json]') || [];
    this.variantMediaMap = this.parseJson('[data-pdp-variant-media-json]') || {};
    this.variantPreorderMap = this.parseJson('[data-pdp-variant-preorder-json]') || {};
    this.updateUrl = root.dataset.updateUrl === 'true';

    // Run each binder in isolation: if one throws (e.g. a malformed metafield
    // or a missing element on an atypical product), the others still attach.
    // Previously an early failure would abort the whole constructor and leave
    // later features — including the how-to video modal — wired to nothing.
    [
      'bindSectionZoom',
      'bindPaneViewportFit',
      'bindGallery',
      'bindGalleryVideos',
      'bindStandaloneAutoplayVideos',
      'bindGalleryZoom',
      'bindConfigure',
      'bindVideoModal',
      'bindHowToHover',
      'bindCompareModal',
      'bindVariantPicker',
      'bindProductOptions',
      'bindVariantGallery',
      'bindCartSubmit',
      'bindGalleryPaneScroll',
      'bindPaneScrollChain',
      'bindSizeChartModal',
      'bindBedsheetQuiz',
      'bindStickyAtcScroll',
    ].forEach((method) => {
      try {
        this[method]();
      } catch (error) {
        console.error(`PdpRedesign: ${method} failed`, error);
      }
    });

    this.updatePaymentTerms();
  }

  parseJson(selector) {
    const node = this.root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  isModalOpen(modal) {
    return Boolean(modal && !modal.hidden);
  }

  getSizeChartModal() {
    return this.sizeChartModal
      || this.root.querySelector('[data-pdp-size-chart-modal]')
      || document.querySelector('[data-pdp-size-chart-modal]');
  }

  syncBodyScrollLock() {
    const locked = this.isModalOpen(this.zoomOverlay)
      || this.isModalOpen(this.videoModal)
      || this.isModalOpen(this.compareModal)
      || this.isModalOpen(this.getSizeChartModal())
      || this.isBedsheetQuizOpen();
    document.body.style.overflow = locked ? 'hidden' : '';
  }

  getBedsheetQuizOverlay() {
    return document.querySelector('.shopify-section.section-bedsheet-quiz')
      || document.querySelector('[data-pdp-bedsheet-quiz-modal]');
  }

  isBedsheetQuizOpen() {
    const overlay = this.getBedsheetQuizOverlay();
    return !!(overlay && overlay.classList.contains('is-open'));
  }

  bindSectionZoom() {
    const grid = this.root.querySelector('[data-pdp-gallery-desktop]');
    if (!grid) return;
    const desktopMq = window.matchMedia('(min-width: 750px)');

    const update = () => {
      if (!desktopMq.matches) {
        this.root.style.zoom = '';
        this.root.style.removeProperty('--pdp-zoom');
        return;
      }

      const styles = window.getComputedStyle(this.root);
      const readVar = (name, fallback) => parseFloat(styles.getPropertyValue(name)) || fallback;
      const infoWidth = readVar('--pdp-info-width', 561);
      const gap = readVar('--pdp-column-gap', 84);
      const viewportWidth = document.documentElement.clientWidth;
      const galleryWidthRaw = styles.getPropertyValue('--pdp-gallery-width').trim();
      const galleryWidth = galleryWidthRaw.endsWith('%')
        ? viewportWidth * (parseFloat(galleryWidthRaw) / 100)
        : (parseFloat(galleryWidthRaw) || viewportWidth * 0.49);
      const needed = galleryWidth + gap + infoWidth;
      const scale = Math.max(Math.min(1, viewportWidth / needed), 0.7);

      if (scale >= 0.999) {
        this.root.style.zoom = '';
        this.root.style.removeProperty('--pdp-zoom');
      } else {
        this.root.style.zoom = String(scale);
        this.root.style.setProperty('--pdp-zoom', String(scale));
      }
    };

    window.addEventListener('resize', update);
    desktopMq.addEventListener('change', update);
    update();
  }

  bindPaneViewportFit() {
    const panes = [
      [this.root.querySelector('.pdp-redesign__gallery-col'), this.root.querySelector('[data-pdp-gallery-col]')],
      [this.root.querySelector('.pdp-redesign__info-col'), this.root.querySelector('[data-pdp-info-col]')],
    ].filter(([col, pane]) => col && pane);
    if (!panes.length) return;
    const desktopMq = window.matchMedia('(min-width: 750px)');

    const update = () => {
      if (!desktopMq.matches) {
        panes.forEach(([, pane]) => { pane.style.maxHeight = ''; });
        return;
      }

      const zoom = parseFloat(this.root.style.zoom) || 1;
      const styles = window.getComputedStyle(this.root);
      const readVar = (name, fallback) => parseFloat(styles.getPropertyValue(name)) || fallback;
      const stickyTop = readVar('--sticky-area-height', 0) + readVar('--pdp-desktop-col-offset', 24);
      panes.forEach(([col, pane]) => {
        const paneTop = Math.max(col.getBoundingClientRect().top, stickyTop);
        const height = (window.innerHeight - paneTop) / zoom;
        pane.style.maxHeight = `${Math.max(height, 200)}px`;
      });
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    desktopMq.addEventListener('change', update);
    this.updatePaneViewportFit = update;
    update();
  }

  bindGallery() {
    const prev = this.root.querySelector('[data-pdp-gallery-prev]');
    const next = this.root.querySelector('[data-pdp-gallery-next]');

    prev?.addEventListener('click', () => this.shiftGallery(-1));
    next?.addEventListener('click', () => this.shiftGallery(1));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.setGalleryIndex(Number(thumb.dataset.pdpGalleryThumb));
      });
    });

    this.desktopItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        this.setGalleryIndex(Number(item.dataset.pdpGalleryDesktopItem));
        const videoWrap = item.querySelector('[data-pdp-gallery-video]');
        if (videoWrap) {
          if (!videoWrap.classList.contains('is-playing')) {
            this.playGalleryVideo(videoWrap);
          }
          return;
        }
        const img = item.querySelector('img');
        if (img) this.openZoom(img);
      });
    });

    const loadMore = this.root.querySelector('[data-pdp-gallery-load-more]');
    loadMore?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.root
        .querySelectorAll('.pdp-gallery__grid-item.is-collapsed, .pdp-gallery__load-more-host.is-collapsed')
        .forEach((item) => item.classList.remove('is-collapsed'));
      loadMore.hidden = true;
    });

    this.galleryTrack?.addEventListener('scroll', () => {
      window.requestAnimationFrame(() => this.syncGalleryUi(this.getActiveIndex()));
    }, { passive: true });

    this.syncGalleryUi(this.getActiveIndex());
  }

  bindGalleryVideos() {
    const muteVideos = this.root.dataset.pdpMuteGalleryVideos === 'true';
    const autoplayVideos = this.root.dataset.pdpAutoplayGalleryVideos === 'true';
    this.root.querySelectorAll('[data-pdp-gallery-video]').forEach((wrap) => {
      const playBtn = wrap.querySelector('[data-pdp-gallery-play]');
      const video = wrap.querySelector('video');
      if (video) {
        const shouldMute = muteVideos || autoplayVideos;
        const shouldLoop = video.hasAttribute('loop') || autoplayVideos;
        video.muted = shouldMute;
        video.controls = false;
        video.loop = shouldLoop;
        video.disablePictureInPicture = true;
        video.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
        video.setAttribute('disablePictureInPicture', '');
        video.setAttribute('disableRemotePlayback', '');
        video.removeAttribute('controls');
        if (shouldLoop) video.setAttribute('loop', '');
        else video.removeAttribute('loop');
        if (shouldMute) video.setAttribute('muted', '');
        else video.removeAttribute('muted');
        if (!shouldLoop) {
          video.addEventListener('ended', () => this.resetGalleryVideo(wrap));
        }
      }
      const toggle = (event) => {
        event.preventDefault();
        this.toggleGalleryVideo(wrap);
      };
      playBtn?.addEventListener('click', toggle);
      playBtn?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleGalleryVideo(wrap);
        }
      });
      wrap.addEventListener('click', toggle);
      if (autoplayVideos) this.autoplayGalleryVideoWhenLaidOut(wrap);
    });
  }

  // The mobile carousel and the desktop grid are both in the DOM and CSS hides one
  // of them, so playing every copy makes the hidden one download in full for
  // nothing. Only start the copy the layout is actually using, and pick the other
  // one up if the breakpoint changes.
  autoplayGalleryVideoWhenLaidOut(wrap) {
    this.whenLaidOut(wrap, (node) => this.playGalleryVideo(node));
  }

  // Gallery slots come through pdp-section-gallery (wrapped in
  // [data-pdp-gallery-video]) while store media comes through pdp-media (a bare
  // video). Both duplicate across the mobile and desktop layouts, so both need the
  // laid-out check; only the wrapped ones have play/pause chrome to sync.
  bindStandaloneAutoplayVideos() {
    this.root.querySelectorAll('video.pdp-media__video--autoplay').forEach((video) => {
      if (video.closest('[data-pdp-gallery-video]')) return;
      this.whenLaidOut(video, (node) => {
        const played = node.play();
        if (played && played.catch) played.catch(() => {});
      });
    });
  }

  whenLaidOut(el, start) {
    if (!el) return;
    if (this.isLaidOut(el)) {
      start(el);
      return;
    }
    this.deferredAutoplayVideos = this.deferredAutoplayVideos || new Map();
    this.deferredAutoplayVideos.set(el, start);
    if (this.deferredAutoplayBound) return;
    this.deferredAutoplayBound = true;
    window.addEventListener('resize', () => {
      window.clearTimeout(this.deferredAutoplayTimer);
      this.deferredAutoplayTimer = window.setTimeout(() => {
        this.deferredAutoplayVideos.forEach((begin, candidate) => {
          if (!this.isLaidOut(candidate)) return;
          this.deferredAutoplayVideos.delete(candidate);
          begin(candidate);
        });
      }, 150);
    }, { passive: true });
  }

  isLaidOut(el) {
    if (!el) return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  toggleGalleryVideo(wrap) {
    if (!wrap) return;
    const video = wrap.querySelector('video');
    if (!video) return;
    if (wrap.classList.contains('is-playing') && !video.paused) {
      video.pause();
      return;
    }
    if (wrap.classList.contains('is-playing') && video.paused) {
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
      return;
    }
    this.playGalleryVideo(wrap);
  }

  playGalleryVideo(wrap) {
    if (!wrap || wrap.classList.contains('is-playing')) return;
    const video = wrap.querySelector('video');
    if (!video) return;
    wrap.classList.add('is-playing');
    const shouldMute = this.root.dataset.pdpMuteGalleryVideos === 'true'
      || this.root.dataset.pdpAutoplayGalleryVideos === 'true';
    video.muted = shouldMute;
    if (shouldMute) video.setAttribute('muted', '');
    video.controls = false;
    video.removeAttribute('controls');
    video.currentTime = 0;
    const played = video.play();
    if (played && played.catch) played.catch(() => {});
  }

  resetGalleryVideo(wrap) {
    if (!wrap) return;
    const video = wrap.querySelector('video');
    wrap.classList.remove('is-playing');
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  }

  syncGalleryUi(index) {
    const slides = this.getSlides();
    const lastIndex = Math.max(0, slides.length - 1);
    const clamped = Math.max(0, Math.min(index, lastIndex));
    const activeSlide = slides[clamped];
    const mediaIndex = activeSlide
      ? Number(activeSlide.dataset.mediaIndex)
      : clamped;

    this.thumbs.forEach((thumb) => {
      thumb.classList.toggle('is-active', Number(thumb.dataset.pdpGalleryThumb) === mediaIndex);
    });
    this.desktopItems.forEach((item) => {
      item.classList.toggle('is-active', Number(item.dataset.pdpGalleryDesktopItem) === mediaIndex);
    });

    const activeThumb = this.thumbs.find(
      (thumb) => Number(thumb.dataset.pdpGalleryThumb) === mediaIndex
    );
    if (activeThumb) {
      this.keepThumbVisible(activeThumb);
    }

    const prev = this.root.querySelector('[data-pdp-gallery-prev]');
    const next = this.root.querySelector('[data-pdp-gallery-next]');
    if (prev) prev.disabled = clamped <= 0;
    if (next) next.disabled = clamped >= lastIndex;
  }

  /* Scrolls the thumbnail rail itself instead of using scrollIntoView. The rail
     only renders on mobile, roughly 1300px down the page, so scrollIntoView's
     `block: 'nearest'` had to pull the whole window down to reveal it — which is
     what made the page jump to the gallery on load, since syncGalleryUi runs
     during init and again on every track scroll event. Adjusting scrollLeft on
     the rail can never move an ancestor scroller. */
  keepThumbVisible(thumb) {
    const strip = thumb.closest('[data-pdp-gallery-thumbs]');
    if (!strip) return;

    const stripRect = strip.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    let left = 0;

    if (thumbRect.left < stripRect.left) {
      left = thumbRect.left - stripRect.left;
    } else if (thumbRect.right > stripRect.right) {
      left = thumbRect.right - stripRect.right;
    }

    if (left) strip.scrollBy({ left, behavior: 'smooth' });
  }

  getSlides() {
    return [...this.galleryTrack?.querySelectorAll('.pdp-gallery__slide:not(.hidden)') || []];
  }

  getActiveIndex() {
    if (!this.galleryTrack) return 0;
    const width = this.galleryTrack.clientWidth || 1;
    return Math.round(this.galleryTrack.scrollLeft / width) || 0;
  }

  setGalleryIndex(index) {
    const slides = this.getSlides();
    if (!slides.length || !this.galleryTrack) return;

    let visibleIndex = slides.findIndex((slide) => Number(slide.dataset.mediaIndex) === index);
    if (visibleIndex < 0) {
      visibleIndex = Math.max(0, Math.min(index, slides.length - 1));
    }

    const target = slides[visibleIndex];
    if (target) {
      const left = this.galleryTrack.scrollLeft
        + (target.getBoundingClientRect().left - this.galleryTrack.getBoundingClientRect().left);
      this.galleryTrack.scrollTo({ left, behavior: 'auto' });
    }
    this.syncGalleryUi(visibleIndex);
  }

  shiftGallery(direction) {
    this.setGalleryIndex(this.getActiveIndex() + direction);
  }

  // Single changing gallery slot: data-pdp-variant-position (1-based) or the
  // marked .is-variant-image tile shows the selected variant image. When
  // custom.gallery_variant_image is set, that override always fills whatever
  // slot currently holds the variant image (including after mobile promote).
  // Shared galleries (e.g. Thermasleep Both/One/Full) swap from
  // data-pdp-option-gallery-json by product id instead.
  bindVariantGallery() {
    this.variantImageMap = this.parseJson('[data-pdp-variant-gallery-json]') || {};

    if (this.root.dataset.pdpHideVariantImage === 'true') {
      this.syncVideoProductImage();
      return;
    }

    const sharedGallery = this.root.dataset.pdpSharedGallery === 'true';
    this.variantGalleryMap = sharedGallery
      ? (this.parseJson('[data-pdp-option-gallery-json]') || {})
      : this.variantImageMap;
    if (!Object.keys(this.variantGalleryMap).length) {
      this.syncVideoProductImage();
      return;
    }

    this.variantSlots = this.resolveVariantSlots();
    if (!this.variantSlots.length) {
      this.syncVideoProductImage();
      return;
    }

    const currentId = sharedGallery
      ? this.root.dataset.pdpProductId
      : (this.variantInput?.value || (this.variants[0] && String(this.variants[0].id)));
    if (currentId) this.updateVariantSlot(currentId);
    this.syncVideoProductImage(currentId);
  }

  resolveVariantSlots() {
    const position = Number(this.root.dataset.pdpVariantPosition) || 1;
    const targets = [
      ['[data-pdp-gallery-track]', '.pdp-gallery__slide'],
      ['[data-pdp-gallery-desktop]', '.pdp-gallery__grid-item'],
      ['[data-pdp-gallery-thumbs]', '.pdp-gallery__thumb'],
    ];
    const slots = [];

    targets.forEach(([containerSel, itemSel]) => {
      const container = this.root.querySelector(containerSel);
      if (!container) return;

      const marked = container.querySelector(`${itemSel}.is-variant-image`);
      if (marked) {
        slots.push(marked);
        return;
      }

      const items = [...container.querySelectorAll(itemSel)].filter(
        (item) => !item.classList.contains('hidden') && !item.hasAttribute('hidden')
      );
      const slot = items[position - 1];
      if (!slot) return;
      slot.classList.add('is-variant-image');
      slots.push(slot);
    });

    return slots;
  }

  updateVariantSlot(variantId) {
    if (this.root.dataset.pdpHideVariantImage === 'true') return;

    const overrideEntry = (this.variantImageMap || {})[variantId]
      || (this.variantImageMap || {})[String(variantId)];
    let entry = overrideEntry && overrideEntry.override
      ? overrideEntry
      : ((this.variantGalleryMap || {})[variantId] || (this.variantGalleryMap || {})[String(variantId)]);

    if (!entry && this.root.dataset.pdpSharedGallery === 'true') {
      const productId = this.root.dataset.pdpProductId;
      entry = (this.variantGalleryMap || {})[productId]
        || (this.variantGalleryMap || {})[String(productId)];
    }

    // Re-resolve so the image follows the slot after mobile promote / reorder.
    this.variantSlots = this.resolveVariantSlots();
    if (!this.variantSlots.length) return;

    this.variantSlots.forEach((slot) => {
      const img = slot.querySelector('img');
      if (!img) return;

      if (!entry || !entry.src) {
        if (slot.classList.contains('is-variant-image') && img.dataset.pdpOriginalSrc) {
          img.src = img.dataset.pdpOriginalSrc;
          if (img.dataset.pdpOriginalSrcset) img.setAttribute('srcset', img.dataset.pdpOriginalSrcset);
          else img.removeAttribute('srcset');
          if (img.dataset.pdpOriginalSizes) img.setAttribute('sizes', img.dataset.pdpOriginalSizes);
        }
        return;
      }

      if (!img.dataset.pdpOriginalSrc) {
        img.dataset.pdpOriginalSrc = img.getAttribute('src') || img.src;
        if (img.getAttribute('srcset')) img.dataset.pdpOriginalSrcset = img.getAttribute('srcset');
        if (img.getAttribute('sizes')) img.dataset.pdpOriginalSizes = img.getAttribute('sizes');
      }

      img.removeAttribute('sizes');
      if (entry.srcset) img.setAttribute('srcset', entry.srcset);
      else img.removeAttribute('srcset');
      img.src = entry.src;
      slot.classList.add('is-variant-image');
    });
  }

  promoteVariantSlotOnMobile() {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    if (this.root.dataset.pdpHideVariantImage === 'true') return;
    if (!this.galleryTrack) return;

    const slide = this.galleryTrack.querySelector('.pdp-gallery__slide.is-variant-image:not(.hidden)');
    if (!slide) return;

    const thumbsRoot = this.root.querySelector('[data-pdp-gallery-thumbs]');
    const thumb = thumbsRoot?.querySelector('.pdp-gallery__thumb.is-variant-image:not(.hidden)')
      || thumbsRoot?.querySelector(`[data-pdp-gallery-thumb="${slide.dataset.mediaIndex}"]`);

    const firstSlide = this.getSlides()[0];
    if (firstSlide && firstSlide !== slide) {
      this.galleryTrack.insertBefore(slide, firstSlide);
    }

    if (thumb && thumbsRoot) {
      const firstThumb = thumbsRoot.querySelector('.pdp-gallery__thumb:not(.hidden)');
      if (firstThumb && firstThumb !== thumb) {
        thumbsRoot.insertBefore(thumb, firstThumb);
      }
    }

    [...this.galleryTrack.querySelectorAll('.pdp-gallery__slide:not(.hidden)')].forEach((el, index) => {
      el.dataset.mediaIndex = String(index);
    });
    if (thumbsRoot) {
      [...thumbsRoot.querySelectorAll('.pdp-gallery__thumb:not(.hidden)')].forEach((el, index) => {
        el.dataset.pdpGalleryThumb = String(index);
      });
    }

    this.thumbs = [...this.root.querySelectorAll('[data-pdp-gallery-thumb]')];
    this.setGalleryIndex(0);
  }

  syncVideoProductImage(variantId) {
    const img = this.root.querySelector('[data-pdp-video-product-image], .pdp-video-modal__product-image img');
    if (!img) return;

    const id = variantId
      || this.variantInput?.value
      || (this.variants[0] && String(this.variants[0].id));
    if (!id) return;

    const map = this.variantImageMap || this.parseJson('[data-pdp-variant-gallery-json]') || {};
    const entry = map[id] || map[String(id)];
    if (!entry || !entry.src) return;

    img.removeAttribute('sizes');
    if (entry.srcset) img.setAttribute('srcset', entry.srcset);
    else img.removeAttribute('srcset');
    img.src = entry.src;
  }

  focusVariantMedia(variantId) {
    const mediaId = this.variantMediaMap[variantId];
    if (!mediaId) return;

    const slide = this.galleryTrack?.querySelector(`.pdp-gallery__slide[data-media-id="${mediaId}"]`);
    if (!slide) return;

    const index = Number(slide.dataset.mediaIndex);
    if (Number.isNaN(index)) return;

    // Mobile hero track + active-state sync (thumbs / desktop grid).
    this.setGalleryIndex(index);

    // Desktop: the grid is capped, so most colour images aren't tiles in it.
    // Swap the hero (first) grid image in place, reusing the already-rendered
    // slide image so every colour updates regardless of the row cap.
    if (window.matchMedia('(min-width: 750px)').matches) {
      const heroImg = this.desktopItems[0]?.querySelector('img');
      const sourceImg = slide.querySelector('img');
      if (heroImg && sourceImg) {
        heroImg.srcset = sourceImg.srcset || '';
        if (sourceImg.sizes) heroImg.sizes = sourceImg.sizes;
        heroImg.src = sourceImg.src;
      }
    }
  }

  bindGalleryZoom() {
    const zoomBtn = this.root.querySelector('[data-pdp-gallery-zoom]');
    if (!zoomBtn) return;

    zoomBtn.addEventListener('click', () => this.openZoom());
    document.addEventListener('keydown', (event) => {
      if (!this.zoomOverlay || this.zoomOverlay.hidden) return;
      if (event.key === 'Escape') this.closeZoom();
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.shiftZoom(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.shiftZoom(1);
      }
    });
  }

  // Current image shown in the mobile hero (falls back to the first slide).
  getActiveImage() {
    const slides = this.getSlides();
    if (!slides.length) return null;
    const active = slides[this.getActiveIndex()] || slides[0];
    return active.querySelector('img');
  }

  getZoomableSlides() {
    return this.getSlides().filter((slide) => {
      if (slide.querySelector('[data-pdp-gallery-video]')) return false;
      return Boolean(slide.querySelector('img'));
    });
  }

  getZoomSlideForImage(img) {
    if (!img) return null;
    const fromSlide = img.closest('.pdp-gallery__slide');
    if (fromSlide) return fromSlide;

    const gridItem = img.closest('[data-pdp-gallery-desktop-item]');
    if (!gridItem) return null;
    const mediaIndex = Number(gridItem.dataset.pdpGalleryDesktopItem);
    return this.getSlides().find((slide) => Number(slide.dataset.mediaIndex) === mediaIndex) || null;
  }

  ensureZoomOverlay() {
    if (this.zoomOverlay) return this.zoomOverlay;

    const overlay = document.createElement('div');
    overlay.className = 'pdp-gallery__zoom-overlay';
    overlay.hidden = true;
    overlay.innerHTML = `
      <div class="pdp-gallery__zoom-toolbar">
        <div class="pdp-gallery__zoom-nav" data-pdp-zoom-nav hidden>
          <button type="button" class="pdp-gallery__arrow" data-pdp-zoom-prev aria-label="Previous image">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M10 3L5 8l5 5"/>
            </svg>
          </button>
          <button type="button" class="pdp-gallery__arrow" data-pdp-zoom-next aria-label="Next image">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 3l5 5-5 5"/>
            </svg>
          </button>
        </div>
        <button type="button" class="pdp-gallery__zoom-close" aria-label="Close zoom">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="pdp-gallery__zoom-stage" data-pdp-zoom-stage>
        <div class="pdp-gallery__zoom-frame" data-pdp-zoom-frame>
          <img class="pdp-gallery__zoom-img" data-pdp-zoom-img alt="">
        </div>
      </div>`;

    overlay.querySelector('.pdp-gallery__zoom-close').addEventListener('click', () => this.closeZoom());
    overlay.querySelector('[data-pdp-zoom-prev]').addEventListener('click', (event) => {
      event.stopPropagation();
      this.shiftZoom(-1);
    });
    overlay.querySelector('[data-pdp-zoom-next]').addEventListener('click', (event) => {
      event.stopPropagation();
      this.shiftZoom(1);
    });
    overlay.addEventListener('click', (event) => {
      if (event.target.closest('[data-pdp-zoom-frame], .pdp-gallery__zoom-toolbar')) return;
      this.closeZoom();
    });
    overlay.querySelector('[data-pdp-zoom-img]').addEventListener('click', (event) => {
      event.stopPropagation();
      event.currentTarget.classList.toggle('is-zoomed');
    });

    document.body.appendChild(overlay);
    this.zoomOverlay = overlay;
    return overlay;
  }

  syncZoomNav() {
    if (!this.zoomOverlay) return;
    const slides = this.getZoomableSlides();
    const nav = this.zoomOverlay.querySelector('[data-pdp-zoom-nav]');
    const prev = this.zoomOverlay.querySelector('[data-pdp-zoom-prev]');
    const next = this.zoomOverlay.querySelector('[data-pdp-zoom-next]');
    const index = this.zoomSlideIndex || 0;
    const lastIndex = Math.max(0, slides.length - 1);

    if (nav) nav.hidden = slides.length < 2;
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= lastIndex;
  }

  showZoomSlide(zoomableIndex, fallbackImg) {
    const slides = this.getZoomableSlides();
    const slide = slides[zoomableIndex];
    const img = slide?.querySelector('img') || fallbackImg;
    if (!img || !this.zoomOverlay) return;

    this.zoomSlideIndex = zoomableIndex;
    const zoomImg = this.zoomOverlay.querySelector('[data-pdp-zoom-img]');
    zoomImg.classList.remove('is-zoomed');
    zoomImg.srcset = img.srcset || '';
    zoomImg.sizes = '100vw';
    zoomImg.src = img.currentSrc || img.src;
    zoomImg.alt = img.alt || '';

    if (slide?.dataset.mediaIndex != null) {
      this.setGalleryIndex(Number(slide.dataset.mediaIndex));
    }
    this.syncZoomNav();
  }

  shiftZoom(direction) {
    if (!this.zoomOverlay || this.zoomOverlay.hidden) return;
    const slides = this.getZoomableSlides();
    if (slides.length < 2) return;
    const next = Math.max(0, Math.min(slides.length - 1, (this.zoomSlideIndex || 0) + direction));
    if (next === this.zoomSlideIndex) return;
    this.showZoomSlide(next);
  }

  openZoom(image) {
    const img = image || this.getActiveImage();
    if (!img) return;

    this.ensureZoomOverlay();

    const slide = this.getZoomSlideForImage(img);
    const zoomable = this.getZoomableSlides();
    let zoomableIndex = slide ? zoomable.indexOf(slide) : -1;
    if (zoomableIndex < 0) {
      zoomableIndex = Math.max(0, this.getActiveIndex());
      const activeSlide = this.getSlides()[zoomableIndex];
      zoomableIndex = activeSlide ? zoomable.indexOf(activeSlide) : 0;
      if (zoomableIndex < 0) zoomableIndex = 0;
    }

    this.showZoomSlide(zoomableIndex, img);
    this.zoomOverlay.hidden = false;
    this.syncBodyScrollLock();
  }

  closeZoom() {
    if (!this.zoomOverlay) return;
    this.zoomOverlay.hidden = true;
    this.zoomOverlay.querySelector('[data-pdp-zoom-img]')?.classList.remove('is-zoomed');
    this.syncBodyScrollLock();
  }

  bindConfigure() {
    const prev = this.root.querySelector('[data-pdp-configure-prev]');
    const next = this.root.querySelector('[data-pdp-configure-next]');
    const track = this.root.querySelector('[data-pdp-configure-track]');

    if (track) {
      track.scrollLeft = 0;
    }

    prev?.addEventListener('click', () => {
      track?.scrollBy({ left: -(track.clientWidth * 0.8), behavior: 'smooth' });
    });

    next?.addEventListener('click', () => {
      track?.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
    });

    this.configureCards.forEach((card) => {
      card.querySelector('[data-pdp-configure-add]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.syncConfigureAddonVariants();
        card.classList.toggle('is-selected');
        this.updateSelectedAddons();
        this.updateCtaPrice();
      });
    });

    this.syncConfigureAddonVariants();
    this.syncConfigureSubtitles();
    window.addEventListener('resize', () => this.syncConfigureSubtitles());
    if (document.fonts?.ready) document.fonts.ready.then(() => this.syncConfigureSubtitles());
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.syncConfigureSubtitles());
      this.configureCards.forEach((card) => {
        const title = card.querySelector('.pdp-configure__card-title');
        if (title) observer.observe(title);
      });
    }
  }

  syncConfigureSubtitles() {
    this.configureCards.forEach((card) => {
      const title = card.querySelector('.pdp-configure__card-title');
      const subtitle = card.querySelector('.pdp-configure__card-subtitle');
      if (!title || !subtitle) return;

      const styles = window.getComputedStyle(title);
      const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5 || 24;
      const lines = Math.round(title.getBoundingClientRect().height / lineHeight);
      subtitle.hidden = lines > 1;
    });
  }

  matchAddonVariant(mainVariant, mainOptionNames, addonOptionNames, addonVariants) {
    if (!mainVariant || !Array.isArray(addonVariants) || !addonVariants.length) return null;

    const mainByName = {};
    (mainOptionNames || []).forEach((name, index) => {
      mainByName[String(name).toLowerCase()] = String(mainVariant.options?.[index] || '').trim().toLowerCase();
    });

    const exact = addonVariants.find((variant) => {
      if (variant.available === false) return false;
      let compared = 0;
      let matches = 0;
      (addonOptionNames || []).forEach((name, index) => {
        const key = String(name).toLowerCase();
        if (!(key in mainByName) || !mainByName[key]) return;
        compared += 1;
        const addonValue = String(variant.options?.[index] || '').trim().toLowerCase();
        if (addonValue && addonValue === mainByName[key]) matches += 1;
      });
      return compared > 0 && matches === compared;
    });
    if (exact) return exact;

    const mainValues = new Set(
      (mainVariant.options || []).map((value) => String(value).trim().toLowerCase()).filter(Boolean)
    );
    return addonVariants.find((variant) => {
      if (variant.available === false) return false;
      return (variant.options || []).some((value) => mainValues.has(String(value).trim().toLowerCase()));
    }) || null;
  }

  syncConfigureAddonVariants() {
    if (!this.configureCards.length) return;

    const mainVariant = this.getCurrentVariant();
    if (!mainVariant) return;

    const mainOptionNames = this.parseJson('[data-pdp-options-json]') || [];

    this.configureCards.forEach((card) => {
      const variantsNode = card.querySelector('[data-pdp-configure-variants]');
      const optionNamesNode = card.querySelector('[data-pdp-configure-option-names]');
      if (!variantsNode) return;

      let addonVariants = [];
      let addonOptionNames = [];
      try {
        addonVariants = JSON.parse(variantsNode.textContent);
        addonOptionNames = optionNamesNode ? JSON.parse(optionNamesNode.textContent) : [];
      } catch (error) {
        return;
      }
      if (!Array.isArray(addonVariants) || addonVariants.length < 2) return;

      const matched = this.matchAddonVariant(mainVariant, mainOptionNames, addonOptionNames, addonVariants);
      if (!matched) return;

      card.dataset.variantId = matched.id;
      card.dataset.addonPrice = matched.price;

      const priceEl = card.querySelector('.pdp-configure__price-current');
      if (priceEl) priceEl.textContent = this.formatMoney(matched.price);

      const mediaOverride = card.querySelector('.pdp-configure__card-media--override');
      const img = card.querySelector('.pdp-configure__card-media img');
      if (!mediaOverride && img && matched.featured_image?.src) {
        img.src = matched.featured_image.src;
        img.srcset = '';
      }
    });
  }

  updateSelectedAddons() {
    this.selectedAddons = this.configureCards
      .filter((card) => card.classList.contains('is-selected'))
      .map((card) => ({
        id: Number(card.dataset.variantId),
        quantity: 1,
        price: parseInt(card.dataset.addonPrice, 10) || 0,
      }));
  }

  // The shop money_format is IE-style `€{{amount}}`, which is wrong for the
  // continental EU markets. themeVariables.settings.moneyFormat carries the
  // per-market format resolved by air-money-format-value, so prefer it. The
  // Liquid that emits it leaves a leading newline, hence the trim.
  get moneyFormat() {
    const themeFormat = window.themeVariables?.settings?.moneyFormat;
    if (themeFormat) return String(themeFormat).trim();
    return typeof currencyFormat !== 'undefined' ? currencyFormat : '';
  }

  // This theme has no global formatMoney, so the substitution is done here
  // rather than falling back to a bare number with no currency symbol.
  formatMoney(cents) {
    const format = this.moneyFormat;
    if (!format) return (cents / 100).toFixed(2);

    // Deliberately NOT delegating to an ambient window.formatMoney. Several
    // reploChunk snippets define a global of that name, and the Replo one wins on
    // the PDP: its placeholder keys are written as Liquid-looking tokens inside a
    // .liquid file, so they render empty, and it returns format.replace('', amount)
    // which prepends the amount and leaves the token behind ("33{{amount_with_comma_separator}} €").
    // The substitution below is self-contained, so the buy box no longer depends on
    // which of those globals happens to load last.
    const placeholder = /\{\{\s*(\w+)\s*\}\}/.exec(format);
    if (!placeholder) return format;

    const group = (value, decimals, thousands, decimal) => {
      const fixed = (Math.abs(Number(cents) || 0) / 100).toFixed(decimals);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };

    let amount;
    switch (placeholder[1]) {
      case 'amount_no_decimals':
        amount = group(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = group(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = group(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = group(cents, 2, "'", '.');
        break;
      default:
        amount = group(cents, 2, ',', '.');
    }

    return format.replace(placeholder[0], amount);
  }

  // EU price display keeps two decimals in every market, so trimming whole
  // amounts is a no-op here. Kept as a seam in case a market wants it back.
  formatMoneyTrim(cents) {
    return this.formatMoney(cents);
  }

  formatInstallmentMoney(cents) {
    return this.formatMoney(cents);
  }

  formatSizeOptionLabel(value) {
    return String(value || '')
      .toLowerCase()
      .split('-')
      .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
      .join('-');
  }

  getCurrentVariant() {
    const id = Number(this.variantInput?.value);
    return this.variants.find((variant) => variant.id === id) || this.variants[0];
  }

  getKlarnaInstallmentCents(totalCents, parts = 3) {
    return Math.round(totalCents / parts / 100) * 100;
  }

  bindProductOptions() {
    const links = [...this.root.querySelectorAll('[data-pdp-product-option]')];
    if (!links.length) return;

    if (!PdpRedesign.popstateBound) {
      PdpRedesign.popstateBound = true;
      window.addEventListener('popstate', () => window.location.reload());
    }

    links.forEach((link) => {
      link.addEventListener('click', async (event) => {
        event.preventDefault();
        const url = link.getAttribute('href');
        if (!url || this.root.classList.contains('is-swapping')) return;

        this.root.classList.add('is-swapping');
        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const newRoot = doc.querySelector('[data-pdp-redesign]');
          if (!newRoot) throw new Error('PDP root missing in response');

          const keepGallery = this.root.dataset.pdpSharedGallery === 'true'
            || newRoot.dataset.pdpSharedGallery === 'true';
          if (keepGallery) {
            const oldGallery = this.root.querySelector('.pdp-redesign__gallery-col');
            const newGallery = newRoot.querySelector('.pdp-redesign__gallery-col');
            if (oldGallery && newGallery) {
              newGallery.replaceWith(oldGallery.cloneNode(true));
            }
            newRoot.dataset.pdpSharedGallery = 'true';
          }

          this.root.replaceWith(newRoot);
          document.querySelectorAll('body > [data-pdp-size-chart-modal]').forEach((node) => node.remove());
          window.history.pushState({}, '', url);
          if (doc.title) document.title = doc.title;
          new PdpRedesign(newRoot);
          newRoot.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        } catch (error) {
          console.error('PdpRedesign: product option swap failed', error);
          window.location.href = url;
        }
      });
    });
  }

  isPreorderVariant(variant) {
    if (this.ctaMode === 'preorder') return true;
    return Boolean(variant && variant.available && this.variantPreorderMap[variant.id]);
  }

  updateCtaPrice() {
    if (this.ctaMode === 'preorder') return;

    const cta = this.root.querySelector('[data-pdp-cta]');
    const variant = this.getCurrentVariant();
    if (!cta || !variant) return;

    if (this.isPreorderVariant(variant)) {
      cta.textContent = cta.dataset.preorderLabel || 'Pre-order today';
      cta.classList.add('pdp-redesign__cta--preorder');
      cta.disabled = false;
      this.updatePaymentTerms();
      return;
    }
    cta.classList.remove('pdp-redesign__cta--preorder');

    const addonTotal = this.selectedAddons.reduce((sum, item) => sum + item.price, 0);
    const total = variant.price + addonTotal;

    if (variant.available) {
      const buyLabel = cta.dataset.buyLabel || 'Buy Now';
      cta.textContent = `${buyLabel} | ${this.formatMoney(total)}`;
      cta.disabled = false;
    }
    this.updatePaymentTerms();
  }

  bindVariantPicker() {
    const picker = this.root.querySelector('[data-pdp-variant-picker]');
    if (!picker) return;

    picker.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = Number(input.dataset.pdpOptionInput);
        const valueLabel = picker.querySelector(`[data-pdp-option-value="${position}"]`);
        if (valueLabel) {
          const optionEl = input.closest('.pdp-variant-picker__option');
          valueLabel.textContent = optionEl?.classList.contains('pdp-variant-picker__option--size')
            ? this.formatSizeOptionLabel(input.value)
            : input.value;
        }

        picker.querySelectorAll('.pdp-variant-picker__swatch').forEach((swatch) => {
          const linked = picker.querySelector(`#${swatch.getAttribute('for')}`);
          swatch.classList.toggle('is-selected', Boolean(linked?.checked));
        });

        this.applySelectedVariant();
      });
    });
  }

  applySelectedVariant() {
    const picker = this.root.querySelector('[data-pdp-variant-picker]');
    if (!picker || !this.variants.length) return;

    const selectedOptions = [];
    for (let i = 1; i <= 3; i += 1) {
      const input = picker.querySelector(`[data-pdp-option-input="${i}"]:checked`);
      if (input) selectedOptions.push(input.value);
    }

    const variant = this.variants.find((item) => {
      return item.options.every((option, index) => option === selectedOptions[index]);
    });

    if (!variant) return;

    if (this.variantInput) this.variantInput.value = variant.id;
    this.syncConfigureAddonVariants();
    this.updateSelectedAddons();
    this.updatePriceUi(variant);
    this.updateVariantSlot(variant.id);
    this.promoteVariantSlotOnMobile();
    this.syncVideoProductImage(variant.id);

    if (this.updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  updatePriceUi(variant) {
    const price = this.root.querySelector('[data-pdp-price]');
    const compare = this.root.querySelector('[data-pdp-compare-price]');
    const saveBadge = this.root.querySelector('[data-pdp-save-badge]');
    const cta = this.root.querySelector('[data-pdp-cta]');

    if (price) price.textContent = this.formatMoney(variant.price);

    const giftValue = Number(this.root.querySelector('[data-pdp-gift-value]')?.dataset.pdpGiftValue) || 0;
    const compareAt = variant.compare_at_price > variant.price ? variant.compare_at_price : 0;
    let beforePrice = giftValue > 0 ? variant.price + giftValue : compareAt;
    let saveAmount = beforePrice > variant.price ? beforePrice - variant.price : 0;

    // Savings round up to whole pounds and the before price is rebuilt from that
    // rounded saving, mirroring the Liquid so the badge and struck-through price
    // stay in step on variant change.
    if (saveAmount > 0) {
      saveAmount = Math.ceil(saveAmount / 100) * 100;
      beforePrice = variant.price + saveAmount;
    }

    if (compare) {
      if (beforePrice > variant.price) {
        compare.textContent = this.formatMoneyTrim(beforePrice);
        compare.hidden = false;
      } else {
        compare.hidden = true;
      }
    }

    if (saveBadge) {
      if (saveAmount > 0) {
        const amount = this.formatMoneyTrim(saveAmount);
        const template = saveBadge.dataset.saveTemplate || 'Save __AMOUNT__';
        saveBadge.textContent = template.replace('__AMOUNT__', amount);
        saveBadge.hidden = false;
      } else {
        saveBadge.textContent = '';
        saveBadge.hidden = true;
      }
    }

    if (cta) {
      if (this.ctaMode === 'preorder') {
        cta.disabled = !variant.available;
      } else if (variant.available) {
        this.updateCtaPrice();
      } else {
        cta.textContent = cta.dataset.soldOutLabel || 'Sold out';
        cta.classList.remove('pdp-redesign__cta--preorder');
        cta.disabled = true;
      }
    }

    this.updatePaymentTerms();
  }

  updatePaymentTerms() {
    const amounts = this.root.querySelectorAll('[data-pdp-klarna-amount]');
    if (!amounts.length) return;

    const variant = this.getCurrentVariant();
    if (!variant) return;

    const addonTotal = this.selectedAddons.reduce((sum, item) => sum + item.price, 0);
    const total = variant.price + addonTotal;

    amounts.forEach((amount) => {
      const parts = Number(amount.getAttribute('data-pdp-klarna-parts') || 3);
      amount.textContent = this.formatInstallmentMoney(this.getKlarnaInstallmentCents(total, parts));
    });
  }

  bindCartSubmit() {
    if (!this.productForm) return;

    const addSelectedItemsToCart = async (event) => {
      if (!this.root.isConnected) return;

      if (event.type === 'click') {
        const cta = event.target.closest?.('[data-pdp-cta]');
        if (!cta || !this.root.contains(cta)) return;
      } else if (event.type === 'submit') {
        if (event.target !== this.productForm) return;
      } else {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isAddingToCart) return;
      this.isAddingToCart = true;

      const cta = this.root.querySelector('[data-pdp-cta]');
      if (cta) cta.disabled = true;

      try {
        const variantId = Number(this.variantInput?.value);
        if (!variantId) return;

        this.updateSelectedAddons();
        const items = [
          { id: variantId, quantity: 1 },
          ...this.selectedAddons
            .filter((item) => Number.isFinite(item.id) && item.id > 0)
            .map(({ id, quantity }) => ({ id, quantity: quantity || 1 })),
        ];

        const response = await fetch(window.Shopify?.routes?.root
          ? `${window.Shopify.routes.root}cart/add.js`
          : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');
          throw new Error(errorBody || 'Add to cart failed');
        }

        if (typeof window.upcartRefreshCart === 'function') {
          try { await window.upcartRefreshCart(); } catch (error) { /* no-op */ }
        }
        if (typeof window.upcartOpenCart === 'function') {
          window.upcartOpenCart();
          return;
        }

        window.location.href = '/cart';
      } catch (error) {
        console.error('PdpRedesign: add to cart failed', error);
        window.location.href = '/cart';
      } finally {
        this.isAddingToCart = false;
        if (cta && this.getCurrentVariant()?.available !== false) cta.disabled = false;
      }
    };

    // Document capture runs before app listeners (e.g. Upcart) that only add the main product.
    document.addEventListener('click', addSelectedItemsToCart, true);
    this.productForm.addEventListener('submit', addSelectedItemsToCart, true);
  }

  bindVideoModal() {
    if (!this.videoModal || !this.videoMedia) return;

    this.videoTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => this.openVideo(index));
    });

    this.root.querySelectorAll('[data-pdp-video-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeVideo());
    });

    this.root.querySelector('[data-pdp-video-add]')?.addEventListener('click', () => {
      this.productForm?.requestSubmit();
      this.closeVideo();
    });

    this.root.querySelector('[data-pdp-video-pause]')?.addEventListener('click', () => this.toggleVideoPlay());
    this.videoMedia.addEventListener('click', () => this.toggleVideoPlay());

    this.root.querySelector('[data-pdp-video-mute]')?.addEventListener('click', () => {
      const video = this.videoMedia.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      this.videoModal.classList.toggle('is-unmuted', !video.muted);
    });

    this.videoModal.querySelector('[data-pdp-video-prev]')?.addEventListener('click', () => this.stepVideo(-1));
    this.videoModal.querySelector('[data-pdp-video-next]')?.addEventListener('click', () => this.stepVideo(1));

    document.addEventListener('keydown', (event) => {
      if (this.videoModal.hidden) {
        if (event.key === 'Escape' && this.compareModal && !this.compareModal.hidden) this.closeCompareModal();
        return;
      }
      if (event.key === 'Escape') this.closeVideo();
      else if (event.key === 'ArrowRight') this.stepVideo(1);
      else if (event.key === 'ArrowLeft') this.stepVideo(-1);
      else if (event.key === ' ') { event.preventDefault(); this.toggleVideoPlay(); }
    });
  }

  // Halo-style hover preview: the inline how-to video fades in and plays while
  // the pointer is over the tile, then resets on leave. Clicking still opens the
  // full popup (wired in bindVideoModal). Touch devices skip this and rely on tap.
  bindHowToHover() {
    if (window.matchMedia('(hover: none)').matches) return;

    this.videoTriggers.forEach((trigger) => {
      const video = trigger.querySelector('[data-pdp-how-to-video]');
      if (!video) return;

      trigger.addEventListener('mouseenter', () => {
        trigger.classList.add('is-playing');
        try {
          video.currentTime = 0;
          const played = video.play();
          if (played && played.catch) played.catch(() => {});
        } catch (error) {
          /* autoplay may be blocked — safe to ignore */
        }
      });

      trigger.addEventListener('mouseleave', () => {
        trigger.classList.remove('is-playing');
        try {
          video.pause();
        } catch (error) {
          /* no-op */
        }
      });
    });
  }

  toggleVideoPlay() {
    const video = this.videoMedia.querySelector('video');
    if (!video) return;
    if (video.paused) video.play();
    else video.pause();
    this.videoModal.classList.toggle('is-paused', video.paused);
  }

  stepVideo(direction) {
    const total = this.videoTriggers.length;
    if (total < 2) return;
    const next = (this.currentVideoIndex + direction + total) % total;
    this.openVideo(next);
  }

  openVideo(index = 0) {
    const trigger = this.videoTriggers[index];
    if (!trigger) return;

    const src = trigger.dataset.videoSrc;
    if (!src) return;

    this.currentVideoIndex = index;
    this.videoModal.classList.remove('is-paused');
    this.videoModal.classList.remove('is-unmuted');
    this.videoModal.classList.remove('is-ready');
    this.videoMedia.innerHTML = '';

    // Shopify-hosted only. Muted + playsInline so autoplay is allowed inline on
    // mobile; the mute control lets the visitor add sound.
    const video = document.createElement('video');
    video.src = src;
    video.playsInline = true;
    video.muted = true;
    video.loop = this.videoTriggers.length < 2;
    this.videoMedia.appendChild(video);
    if (this.videoTriggers.length > 1) {
      video.addEventListener('ended', () => this.stepVideo(1), { once: true });
    }

    // Reveal the video + product card together only once the first frame is
    // decoded, then start playback (playing while opacity:0 would let Chrome
    // pause silent video-only clips to save power). A timeout is a safety net.
    const reveal = () => {
      if (this.videoModal.classList.contains('is-ready')) return;
      this.videoModal.classList.add('is-ready');
      const played = video.play();
      if (played && played.catch) played.catch(() => {});
    };
    if (video.readyState >= 2) {
      reveal();
    } else {
      video.addEventListener('loadeddata', reveal, { once: true });
      setTimeout(reveal, 2500);
    }

    const progress = this.root.querySelector('[data-pdp-video-progress]');
    const total = this.videoTriggers.length;
    if (progress && total > 1) {
      progress.hidden = false;
      progress.innerHTML = '';
      for (let i = 0; i < total; i += 1) {
        const bar = document.createElement('button');
        bar.type = 'button';
        bar.setAttribute('aria-label', `Go to video ${i + 1}`);
        if (i === index) bar.classList.add('is-active');
        bar.addEventListener('click', (e) => { e.stopPropagation(); this.openVideo(i); });
        progress.appendChild(bar);
      }
    } else if (progress) {
      progress.hidden = true;
    }

    if (this.videoProductCard) this.videoProductCard.hidden = false;
    this.videoModal.hidden = false;
    this.syncBodyScrollLock();
  }

  closeVideo() {
    if (!this.videoModal) return;
    this.videoModal.hidden = true;
    if (this.videoMedia) this.videoMedia.innerHTML = '';
    this.syncBodyScrollLock();
  }

  bindCompareModal() {
    this.root.querySelectorAll('[data-pdp-compare-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openCompareModal());
    });

    this.compareModal?.querySelectorAll('[data-pdp-compare-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeCompareModal());
    });

    this.bindCompareCarousel();
  }

  bindCompareCarousel() {
    if (!this.compareModal) return;
    const track = this.compareModal.querySelector('[data-pdp-compare-track]');
    const prev = this.compareModal.querySelector('[data-pdp-compare-prev]');
    const next = this.compareModal.querySelector('[data-pdp-compare-next]');
    const progress = this.compareModal.querySelector('[data-pdp-compare-progress]');
    if (!track) return;

    const step = () => {
      const card = track.querySelector('.pdp-compare-modal__card');
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '16') || 16;
      return card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.5;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      if (progress) {
        const visible = track.scrollWidth > 0 ? track.clientWidth / track.scrollWidth : 1;
        const travelled = max > 0 ? track.scrollLeft / max : 0;
        const fill = Math.min(1, Math.max(visible, 0.15));
        progress.style.width = `${fill * 100}%`;
        progress.style.left = `${travelled * (100 - fill * 100)}%`;
      }
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    this.updateCompareCarousel = update;
  }

  /**
   * The modal is authored inside the section so its markup stays with the buy
   * box, but a fixed-position overlay cannot escape an ancestor that creates a
   * containing block (any transform, filter or contain on the section or on the
   * legacy Replo wrappers around it). When that happens the overlay stops being
   * viewport-anchored and the sections below paint straight over it, which no
   * z-index can fix. Moving the node to <body> on first open sidesteps the whole
   * class of problem regardless of which ancestor is responsible.
   */
  portalCompareModal() {
    if (!this.compareModal || this.compareModal.parentElement === document.body) return;
    document.body.appendChild(this.compareModal);
  }

  openCompareModal() {
    if (!this.compareModal) return;
    this.portalCompareModal();
    this.compareModal.hidden = false;
    this.syncBodyScrollLock();
    this.compareModal.querySelector('.pdp-compare-modal__close')?.focus();
    window.requestAnimationFrame(() => this.updateCompareCarousel?.());
  }

  closeCompareModal() {
    if (!this.compareModal) return;
    this.compareModal.hidden = true;
    this.syncBodyScrollLock();
  }

  /**
   * Desktop dual-pane scroll: both columns are sticky viewport-tall panes
   * that scroll inside themselves, so wheel input over either side moves
   * that side immediately. Gallery rail + arrows track the gallery pane's
   * own scrollTop. Both panes chain into the page once they hit an edge.
   */
  bindGalleryPaneScroll() {
    const galleryCol = this.root.querySelector('[data-pdp-gallery-col]');
    if (!galleryCol) return;

    const desktopMq = window.matchMedia('(min-width: 750px)');
    const galleryRail = this.root.querySelector('[data-pdp-gallery-scroll]');
    const gallerySticky = this.root.querySelector('[data-pdp-gallery-scroll-sticky]');
    const galleryActive = this.root.querySelector('[data-pdp-gallery-scroll-active]');
    const galleryRest = this.root.querySelector('[data-pdp-gallery-scroll-rest]');
    const galleryLine = this.root.querySelector('[data-pdp-gallery-scroll-line]');
    const galleryUp = this.root.querySelector('[data-pdp-gallery-scroll-up]');
    const galleryDown = this.root.querySelector('[data-pdp-gallery-scroll-down]');

    let galleryRailHideTimer;

    const setGalleryRailVisible = (visible) => {
      if (!galleryCol.classList.contains('has-gallery-scroll')) return;
      galleryCol.classList.toggle('is-gallery-rail-visible', visible);
    };

    const pulseGalleryRail = () => {
      setGalleryRailVisible(true);
      window.clearTimeout(galleryRailHideTimer);
      galleryRailHideTimer = window.setTimeout(() => {
        if (!galleryCol.matches(':hover')) setGalleryRailVisible(false);
      }, 1500);
    };

    const updateGalleryRail = () => {
      if (!galleryRail || !galleryActive || !galleryRest || !galleryLine) return;

      if (!desktopMq.matches) {
        galleryCol.classList.remove('has-gallery-scroll', 'is-gallery-rail-visible');
        galleryRail.hidden = true;
        if (gallerySticky) gallerySticky.hidden = true;
        return;
      }

      const maxScroll = galleryCol.scrollHeight - galleryCol.clientHeight;
      if (maxScroll <= 4) {
        galleryCol.classList.remove('has-gallery-scroll', 'is-gallery-rail-visible');
        galleryRail.hidden = true;
        if (gallerySticky) gallerySticky.hidden = true;
        return;
      }

      galleryCol.classList.add('has-gallery-scroll');
      galleryRail.hidden = false;
      if (gallerySticky) gallerySticky.hidden = false;

      const progress = galleryCol.scrollTop / maxScroll;
      const lineStyles = window.getComputedStyle(galleryLine);
      const linePad =
        (parseFloat(lineStyles.paddingTop) || 0) + (parseFloat(lineStyles.paddingBottom) || 0);
      const lineHeight = Math.max(galleryLine.clientHeight - linePad, 2);
      const activeHeight = Math.max(progress * lineHeight, 2);
      galleryActive.style.height = `${activeHeight}px`;
      galleryRest.style.height = `${Math.max(lineHeight - activeHeight, 2)}px`;

      if (galleryUp) galleryUp.disabled = galleryCol.scrollTop <= 1;
      if (galleryDown) galleryDown.disabled = galleryCol.scrollTop >= maxScroll - 1;
    };

    galleryCol.addEventListener('mouseenter', () => setGalleryRailVisible(true));
    galleryCol.addEventListener('mouseleave', () => setGalleryRailVisible(false));
    galleryCol.addEventListener('scroll', () => {
      updateGalleryRail();
      pulseGalleryRail();
    }, { passive: true });

    galleryUp?.addEventListener('click', () => {
      galleryCol.scrollBy({ top: -galleryCol.clientHeight * 0.75, behavior: 'smooth' });
      pulseGalleryRail();
    });
    galleryDown?.addEventListener('click', () => {
      galleryCol.scrollBy({ top: galleryCol.clientHeight * 0.75, behavior: 'smooth' });
      pulseGalleryRail();
    });

    window.addEventListener('resize', updateGalleryRail);
    desktopMq.addEventListener('change', updateGalleryRail);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateGalleryRail);
      observer.observe(galleryCol);
      const infoCol = this.root.querySelector('[data-pdp-info-col]');
      if (infoCol) observer.observe(infoCol);
    }

    this.root.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateGalleryRail, { once: true });
    });
    if (document.fonts?.ready) document.fonts.ready.then(updateGalleryRail);

    updateGalleryRail();
    window.requestAnimationFrame(updateGalleryRail);
  }

  bindPaneScrollChain() {
    const panes = [
      this.root.querySelector('[data-pdp-info-col]'),
      this.root.querySelector('[data-pdp-gallery-col]'),
    ].filter(Boolean);
    if (!panes.length) return;

    const desktopMq = window.matchMedia('(min-width: 750px)');

    const chainPane = (pane) => {
      pane.addEventListener('wheel', (event) => {
        if (!desktopMq.matches) return;
        if (event.ctrlKey) return;

        let delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16;
        else if (event.deltaMode === 2) delta *= window.innerHeight;
        if (delta === 0) return;

        const maxScroll = pane.scrollHeight - pane.clientHeight;
        if (maxScroll <= 1) {
          event.preventDefault();
          window.scrollBy(0, delta);
          return;
        }

        const atTop = pane.scrollTop <= 0;
        const atBottom = pane.scrollTop >= maxScroll - 1;
        const scrollingDown = delta > 0;
        const scrollingUp = delta < 0;

        if ((scrollingDown && atBottom) || (scrollingUp && atTop)) {
          event.preventDefault();
          window.scrollBy(0, delta);
        }
      }, { passive: false });
    };

    panes.forEach(chainPane);
  }

  bindSizeChartModal() {
    const modal = this.root.querySelector('[data-pdp-size-chart-modal]');
    if (!modal) return;

    this.root.querySelectorAll('[data-pdp-size-chart-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.openSizeChartModal());
    });

    modal.querySelectorAll('[data-pdp-size-chart-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeSizeChartModal());
    });

    modal.querySelector('[data-pdp-size-chart-video-open]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.openSizeChartVideo();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isModalOpen(modal)) this.closeSizeChartModal();
    });

    this.renderSizeChartTable(modal);
  }

  renderSizeChartTable(modal) {
    const wrap = modal.querySelector('[data-pdp-size-chart-table]');
    if (!wrap) return;

    let data;
    try {
      data = JSON.parse(wrap.getAttribute('data-pdp-size-chart-table'));
    } catch (error) {
      return;
    }
    if (!data || !Array.isArray(data.columns) || !Array.isArray(data.sizes)) return;

    const esc = (value) => String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const hasCms = data.cms && typeof data.cms === 'object';
    const hasInches = data.inches && typeof data.inches === 'object';
    let unit = hasCms ? 'cms' : 'inches';

    const buildTable = () => {
      const units = data[unit] || {};
      let html = '<div class="pdp-size-chart-modal__table-inner"><table class="pdp-size-chart-modal__table"><thead><tr>';
      data.columns.forEach((col) => { html += `<th>${esc(col.label)}</th>`; });
      html += '</tr></thead><tbody>';
      data.sizes.forEach((size, rowIndex) => {
        html += '<tr>';
        data.columns.forEach((col) => {
          if (col.key === 'size') {
            html += `<td>${esc(size)}</td>`;
          } else {
            const values = units[col.key] || [];
            html += `<td>${esc(values[rowIndex] || '')}</td>`;
          }
        });
        html += '</tr>';
      });
      return `${html}</tbody></table></div>`;
    };

    const render = () => {
      let toggle = '';
      if (hasCms && hasInches) {
        toggle = '<div class="pdp-size-chart-modal__tabs" role="tablist" aria-label="Units">'
          + `<button type="button" role="tab" data-unit="cms" class="pdp-size-chart-modal__tab${unit === 'cms' ? ' is-active' : ''}" aria-selected="${unit === 'cms'}">CMS</button>`
          + `<button type="button" role="tab" data-unit="inches" class="pdp-size-chart-modal__tab${unit === 'inches' ? ' is-active' : ''}" aria-selected="${unit === 'inches'}">INCHES</button>`
          + '</div>';
      } else if (hasCms || hasInches) {
        const label = hasCms ? 'CMS' : 'INCHES';
        toggle = `<div class="pdp-size-chart-modal__tabs" role="tablist" aria-label="Units"><button type="button" role="tab" class="pdp-size-chart-modal__tab is-active" aria-selected="true">${label}</button></div>`;
      }
      wrap.innerHTML = toggle + buildTable();
      wrap.querySelectorAll('[data-unit]').forEach((btn) => {
        btn.addEventListener('click', () => {
          unit = btn.getAttribute('data-unit');
          render();
        });
      });
    };

    render();
  }

  openSizeChartModal() {
    const modal = this.getSizeChartModal();
    if (!modal) return;

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
    this.sizeChartModal = modal;

    modal.hidden = false;
    modal.classList.remove('is-video');
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (video) video.hidden = true;
    this.syncBodyScrollLock();
    modal.querySelector('.pdp-size-chart-modal__close')?.focus();
  }

  openSizeChartVideo() {
    const modal = this.getSizeChartModal();
    if (!modal) return;
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (!video) return;
    modal.classList.add('is-video');
    video.hidden = false;
  }

  closeSizeChartModal() {
    const modal = this.getSizeChartModal();
    if (!modal) return;
    modal.hidden = true;
    modal.classList.remove('is-video');
    const video = modal.querySelector('[data-pdp-size-chart-video]');
    if (video) {
      video.hidden = true;
      video.querySelectorAll('video').forEach((node) => node.pause());
      video.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.src;
        iframe.src = src;
      });
    }
    this.syncBodyScrollLock();
  }

  bindBedsheetQuiz() {
    const triggers = this.root.querySelectorAll('[data-pdp-bedsheet-quiz-open]');
    if (!triggers.length) return;

    triggers.forEach((btn) => {
      if (btn.dataset.pdpBedsheetQuizBound === 'true') return;
      btn.dataset.pdpBedsheetQuizBound = 'true';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.openBedsheetQuiz();
      });
    });

    if (PdpRedesign.bedsheetQuizGlobalBound) return;
    PdpRedesign.bedsheetQuizGlobalBound = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.quiz-popup-close-icon, [data-close-icon="close-icon"], [data-pdp-bedsheet-quiz-close]')) {
        return;
      }
      PdpRedesign.closeBedsheetQuizOverlay();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      PdpRedesign.closeBedsheetQuizOverlay();
    });
  }

  openBedsheetQuiz() {
    const overlay = this.getBedsheetQuizOverlay();
    if (!overlay) return;

    if (overlay.hasAttribute('hidden')) overlay.hidden = false;
    overlay.classList.add('is-open');
    overlay.style.removeProperty('display');

    this.syncBodyScrollLock();
  }

  closeBedsheetQuiz() {
    PdpRedesign.closeBedsheetQuizOverlay();
    this.syncBodyScrollLock();
  }

  static closeBedsheetQuizOverlay() {
    const overlay = document.querySelector('.shopify-section.section-bedsheet-quiz')
      || document.querySelector('[data-pdp-bedsheet-quiz-modal]');
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.style.removeProperty('display');
    if (overlay.hasAttribute('data-pdp-bedsheet-quiz-modal')) overlay.hidden = true;
    document.body.style.overflow = '';
  }

  bindStickyAtcScroll() {
    if (PdpRedesign.stickyAtcScrollBound) return;
    PdpRedesign.stickyAtcScrollBound = true;

    if (!PdpRedesign.nativeScrollIntoView) {
      PdpRedesign.nativeScrollIntoView = Element.prototype.scrollIntoView;
    }
    const nativeScrollIntoView = PdpRedesign.nativeScrollIntoView;

    const scrollToBuy = () => {
      const redesign = document.querySelector('.pdp-redesign-wrapper');
      const target = document.getElementById('buy')
        || (redesign && redesign.querySelector('[data-pdp-variant-picker]'))
        || (redesign && redesign.querySelector('.pdp-redesign__info'))
        || redesign;
      if (!target) return;
      const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - 12);
      window.scrollTo(0, top);
    };

    Element.prototype.scrollIntoView = function (...args) {
      const redesign = document.querySelector('.pdp-redesign-wrapper');
      if (redesign) {
        const isHiddenProduct = this.id === 'Product'
          || (typeof this.closest === 'function' && this.closest('#Product'));
        if (isHiddenProduct) {
          scrollToBuy();
          return;
        }
      }
      return nativeScrollIntoView.apply(this, args);
    };

    const onUpdateIntent = (event) => {
      if (!document.querySelector('.pdp-redesign-wrapper')) return;

      const link = event.target.closest('[role="link"]');
      if (!link) return;

      const bar = link.closest('[data-replo-product-container]');
      if (!bar || !bar.querySelector('[data-replo-add-product-variant-to-cart]')) return;
      if (!(link.textContent || '').toLowerCase().includes('update')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scrollToBuy();
    };

    document.addEventListener('click', onUpdateIntent, true);
    document.addEventListener('pointerdown', onUpdateIntent, true);
  }
}

document.querySelectorAll('[data-pdp-redesign]').forEach((root) => {
  if (root.dataset.updateUrl === undefined) {
    root.dataset.updateUrl = 'true';
  }
  new PdpRedesign(root);
});
